import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useRecoveryConfirmationStore, useRecoveryStore } from '@lib/store';
import { useInterval } from '@shared/hooks';
import { PaletteUtils } from '@shared/utils';
import { IForm as IStepOneForm } from './stepOne';
import { Button, TextInput } from '@shared/widgets';

interface IForm {
  password: string;
  retypePassword: string;
  confirmCode: string;
}
const DEFAULT_VALUES: IForm = { password: '', retypePassword: '', confirmCode: '' };

interface StepTwoProps {
  className?: string;
  recoveryData: IStepOneForm;
  onSubmit?: () => void;
}

export const StepTwo = (props: StepTwoProps) => {
  const recoveryStore = useRecoveryStore();
  const recoveryConfirmationStore = useRecoveryConfirmationStore();

  const [count, setCount] = useState<number>(60);

  const { control, formState, handleSubmit, watch, setError } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const password = watch('password');
  const retypePassword = watch('retypePassword');

  const { t } = useTranslation('remember');

  useEffect(() => {
    if (password === retypePassword) setError('retypePassword', { message: '' });
  }, [password]);

  useInterval(() => setCount((prev) => prev - 1), count ? 1000 : null);
  const resendHandler = async () => {
    recoveryStore.recovery({ username: props.recoveryData.username, cardLast4Digits: props.recoveryData.card });
    setCount(60);
  };
  const submitHandler = async (data: IForm) => {
    const result = await recoveryConfirmationStore.recoveryConfirmation({ newPassword: data.password, code: data.confirmCode });
    if (result.error) {
      setError('confirmCode', { message: result.error });
      return;
    }
    props.onSubmit?.();
  };

  const isDisabled = !watch('password') || !watch('retypePassword') || !watch('confirmCode');
  return (
    <>
      <p className='text-base text-center text-[#dadada] mb-7'>{t('stepTwo.description')}</p>
      <form className={props.className} onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='password'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            pattern: {
              value: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?]).+$/,
              message: t('errors.form.password.invalidFormat', { ns: 'common' }),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              inputClassName={clsx(
                'hover:border-black transition-all ease  duration-300',
                PaletteUtils.isBordierPalette ? 'bg-[#f8f8f8]' : 'bg-[#303034]',
              )}
              captionClassName='!text-base !font-light'
              caption={t('stepTwo.placeholders.newPassword')}
              name='password'
              type='password'
              required
              error={error?.message}
            />
          )}
        />
        <Controller
          name='retypePassword'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            validate: (value) => value === password || t('errors.form.password.mustEqual', { ns: 'common' }),
          }}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              inputClassName={clsx(
                'hover:border-black transition-all ease  duration-300',
                PaletteUtils.isBordierPalette ? 'bg-[#f8f8f8]' : 'bg-[#303034]',
              )}
              captionClassName='!text-base !font-light'
              caption={t('stepTwo.placeholders.retypeNewPassword')}
              name='retypePassword'
              type='password'
              required
              error={error?.message}
            />
          )}
        />
        <div className='inline-block'>
          <Controller
            name='confirmCode'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                inputClassName={clsx(
                  'hover:border-black transition-all ease duration-300',
                  PaletteUtils.isBordierPalette ? 'bg-[#f8f8f8]' : 'bg-[#303034]',
                )}
                captionClassName='!text-base !font-light'
                caption={`${t('placeholders.confirmationCode', { ns: 'common' })}:`}
                name='confirmCode'
                required
                error={error?.message}
              />
            )}
          />
          <Button
            className='mt-8 h-[50px] text-[#dadada] text-xs font-medium !float-right max-[840px]:h-8 max-[840px]mt-0:'
            caption={
              <span>
                {t('buttons.resend', { ns: 'common' })}
                {count > 0 && <span className='text-inherit'>{` (${count})`}</span>}
              </span>
            }
            disabled={count > 0 || recoveryStore.loading}
            onClick={resendHandler}
            onKeyDown={(e) => e.keyCode === 13 && resendHandler()}
          />
        </div>
        <Button
          className='mt-[92px] text-base h-[50px]'
          fullWidth
          caption={t('buttons.next', { ns: 'common' })}
          type='submit'
          disabled={isDisabled || formState.isSubmitting}
        />
      </form>
    </>
  );
};
